/* AJ Customer Survey
   Renders one question per screen from the section's JSON config ([data-ajsv-config]).
   Answers are saved in localStorage as the shopper goes, so a closed tab resumes where it stopped.
   A logged-in customer is greeted by name and never asked for a detail their account already has.
   On finish: one Klaviyo "Completed Customer Survey" event holding every answer plus name, email and
   phone (the survey dashboard and its CSV read these events), the same answers on the profile as svy_*
   properties, and the phone on the profile in a separate call so a number Klaviyo rejects can never
   cost us the response. dry_run (theme editor / local preview) sends nothing. */
(function () {
  'use strict';

  var KLAVIYO_REVISION = '2024-10-15';
  var AUTO_ADVANCE_MS = 420;
  var SEND_WAIT_MS = 3000;
  var PIN_LAST = /^(other|something else|none|prefer not|no favorite|not sure|only from alex|i'd rather)/i;
  var EXCLUSIVE = /^(none|prefer not|only from alex)/i;
  var CHECK = '<svg viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="3.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7.5"/></svg>';
  var BIG_CHECK = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7.5"/></svg>';
  var GIFT = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 8h18v4H3z"/><path d="M12 8v13"/><path d="M19 12v7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-7"/><path d="M7.5 8a2.5 2.5 0 0 1 0-5C11 3 12 8 12 8s1-5 4.5-5a2.5 2.5 0 0 1 0 5"/></svg>';
  var COPY = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="9" y="9" width="12" height="12" rx="2"/><path d="M5 15V5a2 2 0 0 1 2-2h10"/></svg>';
  var ARROW = '<span class="ajsv__arrow" aria-hidden="true">&rarr;</span>';

  function esc(v) {
    return String(v == null ? '' : v).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function list(s) {
    return String(s || '').split('|').map(function (x) { return x.trim(); }).filter(Boolean);
  }
  function fill(tpl, map) {
    return String(tpl || '').replace(/\{(\w+)\}/g, function (m, k) { return map[k] != null ? map[k] : m; });
  }
  function reducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }
  function isTyping(el) {
    if (!el) return false;
    var t = (el.tagName || '').toLowerCase();
    return t === 'input' || t === 'textarea' || t === 'select' || el.isContentEditable;
  }
  function digits(s) { return String(s || '').replace(/\D+/g, ''); }
  // US numbers only become E.164 (+1XXXXXXXXXX). Anything else stays out of the profile field.
  function e164(s) {
    var d = digits(s);
    if (d.length === 11 && d.charAt(0) === '1') d = d.slice(1);
    return d.length === 10 && /^[2-9]\d{2}[2-9]/.test(d) ? '+1' + d : '';
  }
  function prettyPhone(s) {
    var d = digits(s);
    if (d.length === 11 && d.charAt(0) === '1') d = d.slice(1);
    if (d.length > 10) return s;
    if (d.length > 6) return '(' + d.slice(0, 3) + ') ' + d.slice(3, 6) + '-' + d.slice(6);
    if (d.length > 3) return '(' + d.slice(0, 3) + ') ' + d.slice(3);
    return d;
  }
  function uid() {
    if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID();
    return 'r' + Date.now().toString(36) + Math.random().toString(36).slice(2, 10);
  }
  function titleCase(s) {
    s = String(s || '').trim();
    return s ? s.charAt(0).toUpperCase() + s.slice(1) : '';
  }

  function normalize(q, i) {
    var kind = q.kind || 'single';
    return {
      i: i,
      group: q.group || '',
      q: q.q || '',
      kind: kind,
      key: String(q.key || '').trim(),
      opts: kind === 'scale' ? ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10'] : list(q.options),
      layout: q.layout || 'list',
      max: parseInt(q.max, 10) || 0,
      optional: !!q.optional,
      shuffle: !!q.shuffle,
      prefillState: !!q.prefill_state,
      help: q.help || '',
      showIf: q.show_if_key ? { key: String(q.show_if_key).trim(), vals: list(q.show_if_values) } : null
    };
  }

  function killPostscript() {
    var html = document.documentElement;
    html.classList.add('ajsv-no-ps');
    function strip() {
      if (html.classList.contains('ps-scroll-lock')) html.classList.remove('ps-scroll-lock');
      if (document.body && document.body.classList.contains('ps-scroll-lock')) document.body.classList.remove('ps-scroll-lock');
    }
    strip();
    if (window.MutationObserver) {
      var mo = new MutationObserver(strip);
      mo.observe(html, { attributes: true, attributeFilter: ['class'] });
      if (document.body) mo.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    }
  }

  function confetti() {
    if (reducedMotion() || !window.requestAnimationFrame) return;
    var cv = document.createElement('canvas');
    cv.className = 'ajsv-confetti';
    cv.setAttribute('aria-hidden', 'true');
    document.body.appendChild(cv);
    var ctx = cv.getContext('2d');
    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    var W = cv.width = window.innerWidth * dpr;
    var H = cv.height = window.innerHeight * dpr;
    var colors = ['#E1241C', '#0A0A0A', '#E5A830', '#B8B8B4', '#E1241C'];
    var bits = [];
    for (var i = 0; i < 160; i++) {
      var fromLeft = i % 2 === 0;
      bits.push({
        x: (fromLeft ? 0.08 : 0.92) * W, y: 0.62 * H,
        vx: (fromLeft ? 1 : -1) * (4 + Math.random() * 9) * dpr, vy: -(10 + Math.random() * 13) * dpr,
        w: (6 + Math.random() * 7) * dpr, h: (9 + Math.random() * 9) * dpr,
        r: Math.random() * 6.28, vr: (Math.random() - 0.5) * 0.35, c: colors[i % colors.length]
      });
    }
    var start = null;
    function tick(t) {
      if (!start) start = t;
      var age = t - start;
      ctx.clearRect(0, 0, W, H);
      for (var i = 0; i < bits.length; i++) {
        var b = bits[i];
        b.vy += 0.42 * dpr; b.vx *= 0.99; b.x += b.vx; b.y += b.vy; b.r += b.vr;
        ctx.save();
        ctx.globalAlpha = Math.max(0, 1 - Math.max(0, age - 2200) / 900);
        ctx.translate(b.x, b.y); ctx.rotate(b.r);
        ctx.fillStyle = b.c;
        ctx.fillRect(-b.w / 2, -b.h / 2, b.w, b.h * Math.abs(Math.cos(b.r * 1.7)));
        ctx.restore();
      }
      if (age < 3200) window.requestAnimationFrame(tick);
      else if (cv.parentNode) cv.parentNode.removeChild(cv);
    }
    window.requestAnimationFrame(tick);
  }

  /* ------------------------------------------------------------------ */

  function Survey(root) {
    this.root = root;
    this.stage = root.querySelector('[data-ajsv-stage]');
    this.card = root.querySelector('[data-ajsv-card]');
    this.cfg = JSON.parse(root.querySelector('[data-ajsv-config]').textContent);
    this.cust = this.cfg.customer || {};
    this.qs = (this.cfg.questions || []).map(normalize).filter(function (q) { return q.key && q.q; });
    this.byKey = {};
    for (var i = 0; i < this.qs.length; i++) this.byKey[this.qs[i].key] = this.qs[i];
    this.storeKey = 'ajsv:' + (this.cfg.storage_key || 'v2');
    this.timer = null;
    this.state = this.load();
    this.view = this.state.done ? 'done' : 'intro';
    if (this.cfg.hide_postscript) killPostscript();
    this.bindKeys();
    this.render(false);
  }

  Survey.prototype.fresh = function () {
    return { step: 'q', idx: -1, answers: {}, orders: {}, contact: {}, optin: null, done: false, prefilled: {}, rid: uid() };
  };

  Survey.prototype.load = function () {
    var s = null;
    try { s = JSON.parse(window.localStorage.getItem(this.storeKey) || 'null'); } catch (e) { s = null; }
    if (!s || typeof s !== 'object' || !s.answers) return this.fresh();
    var clean = {};
    for (var k in s.answers) if (this.byKey[k]) clean[k] = s.answers[k];
    s.answers = clean;
    s.orders = s.orders || {};
    s.contact = s.contact || {};
    s.prefilled = s.prefilled || {};
    s.rid = s.rid || uid();
    return s;
  };

  Survey.prototype.save = function () {
    try { window.localStorage.setItem(this.storeKey, JSON.stringify(this.state)); } catch (e) { /* private mode */ }
  };

  /* ---------- who is this ---------- */

  Survey.prototype.firstName = function () {
    return titleCase(this.cust.first_name || this.state.contact.first_name || '');
  };

  // Contact fields their Shopify account doesn't already give us
  Survey.prototype.missing = function () {
    var c = this.cust, need = [];
    if (!String(c.first_name || '').trim()) need.push('first_name');
    if (!String(c.last_name || '').trim()) need.push('last_name');
    if (!String(c.email || '').trim()) need.push('email');
    if (digits(c.phone).length < 10) need.push('phone');
    return need;
  };

  /* ---------- question bookkeeping ---------- */

  Survey.prototype.answer = function (q) { return this.state.answers[q.key]; };

  Survey.prototype.hasAnswer = function (q) {
    var a = this.answer(q);
    if (a == null) return false;
    if (Array.isArray(a)) return a.length > 0;
    return String(a).trim() !== '';
  };

  Survey.prototype.isShown = function (q) {
    if (!q.showIf) return true;
    var a = this.state.answers[q.showIf.key];
    if (a == null) return false;
    var arr = Array.isArray(a) ? a : [a];
    for (var i = 0; i < arr.length; i++) if (q.showIf.vals.indexOf(String(arr[i])) > -1) return true;
    return false;
  };

  Survey.prototype.visible = function () {
    var self = this;
    return this.qs.filter(function (q) { return self.isShown(q); });
  };

  Survey.prototype.nextIdx = function (from) {
    for (var i = from + 1; i < this.qs.length; i++) if (this.isShown(this.qs[i])) return i;
    return -1;
  };

  Survey.prototype.prevIdx = function (from) {
    for (var i = from - 1; i >= 0; i--) if (this.isShown(this.qs[i])) return i;
    return -1;
  };

  Survey.prototype.isLast = function (q) { return this.nextIdx(q.i) < 0; };

  Survey.prototype.groups = function () {
    var g = [];
    this.visible().forEach(function (q) { if (g.indexOf(q.group) === -1) g.push(q.group); });
    return g;
  };

  Survey.prototype.options = function (q) {
    if (!q.shuffle) return q.opts;
    var saved = this.state.orders[q.key];
    if (saved && saved.length === q.opts.length && saved.every(function (o) { return q.opts.indexOf(o) > -1; })) return saved;
    var free = q.opts.filter(function (o) { return !PIN_LAST.test(o); });
    var pinned = q.opts.filter(function (o) { return PIN_LAST.test(o); });
    for (var i = free.length - 1; i > 0; i--) {
      var j = Math.floor(Math.random() * (i + 1));
      var t = free[i]; free[i] = free[j]; free[j] = t;
    }
    var order = free.concat(pinned);
    this.state.orders[q.key] = order;
    this.save();
    return order;
  };

  // Pre-select the state from their Shopify address, once, the first time they reach that question
  Survey.prototype.prefill = function (q) {
    if (!q.prefillState || this.hasAnswer(q) || this.state.prefilled[q.key]) return;
    var st = String(this.cust.state || '').trim();
    if (st && q.opts.indexOf(st) > -1) {
      this.state.answers[q.key] = st;
      this.state.prefilled[q.key] = st;
      this.save();
    }
  };

  /* ---------- navigation ---------- */

  Survey.prototype.go = function (view, scroll) {
    clearTimeout(this.timer);
    this.view = view;
    if (view === 'q' || view === 'contact') this.state.step = view;
    this.save();
    this.render(scroll !== false);
  };

  Survey.prototype.start = function (resume) {
    if (!resume) {
      var keepOrders = this.state.orders;
      this.state = this.fresh();
      this.state.orders = keepOrders || {};
    }
    if (resume && this.state.step === 'contact') return this.go('contact');
    var idx = this.state.idx;
    if (idx < 0 || idx >= this.qs.length || !this.isShown(this.qs[idx])) idx = this.nextIdx(Math.max(-1, idx - 1));
    if (idx < 0) idx = this.nextIdx(-1);
    this.state.idx = idx;
    if (idx < 0) return this.afterQuestions();
    this.go('q');
  };

  Survey.prototype.afterQuestions = function () {
    if (this.missing().length) this.go('contact');
    else this.finish(null);
  };

  Survey.prototype.next = function () {
    var n = this.nextIdx(this.state.idx);
    if (n < 0) { this.afterQuestions(); return; }
    this.state.idx = n;
    this.go('q');
  };

  Survey.prototype.back = function () {
    if (this.view === 'contact') {
      var last = this.prevIdx(this.qs.length);
      if (last > -1) { this.state.idx = last; this.go('q'); } else { this.go('intro'); }
      return;
    }
    var p = this.prevIdx(this.state.idx);
    if (p < 0) { this.go('intro'); return; }
    this.state.idx = p;
    this.go('q');
  };

  /* ---------- rendering ---------- */

  Survey.prototype.render = function (scroll) {
    var html = '';
    if (this.view === 'intro') html = this.introHTML();
    else if (this.view === 'q') html = this.questionHTML(this.qs[this.state.idx]);
    else if (this.view === 'contact') html = this.contactHTML();
    else html = this.doneHTML();
    this.card.setAttribute('data-view', this.view);
    this.stage.innerHTML = '<div class="ajsv__screen" data-view="' + this.view + '">' + html + '</div>';
    this.bind();
    var h = this.stage.querySelector('[data-ajsv-focus]');
    if (h && scroll) { try { h.focus({ preventScroll: true }); } catch (e) { h.focus(); } }
    if (scroll) this.ensureVisible();
  };

  Survey.prototype.headerBottom = function () {
    var max = 0;
    var els = document.querySelectorAll('.ajh-scope, .ajh-scope > *, .ajh-top, sticky-header, .section-header, header');
    for (var i = 0; i < els.length; i++) {
      var cs = window.getComputedStyle(els[i]);
      if (cs.position !== 'fixed' && cs.position !== 'sticky') continue;
      var r = els[i].getBoundingClientRect();
      if (r.top <= 1 && r.bottom > max && r.bottom < window.innerHeight * 0.5) max = r.bottom;
    }
    return max;
  };

  Survey.prototype.ensureVisible = function () {
    var r = this.card.getBoundingClientRect();
    var off = this.headerBottom();
    if (r.top >= off && r.top < window.innerHeight * 0.4) return;
    var y = window.pageYOffset + r.top - off - 10;
    try { window.scrollTo({ top: Math.max(0, y), behavior: reducedMotion() ? 'auto' : 'smooth' }); }
    catch (e) { window.scrollTo(0, Math.max(0, y)); }
  };

  Survey.prototype.topHTML = function (count, pct) {
    return '<div class="ajsv__top">' +
        '<button type="button" class="ajsv__link" data-ajsv-back><span aria-hidden="true">&larr;</span> Back</button>' +
        '<span class="ajsv__count">' + esc(count) + '</span>' +
      '</div>' +
      '<div class="ajsv__bar" role="progressbar" aria-label="Survey progress" aria-valuemin="0" aria-valuemax="100" aria-valuenow="' + Math.round(pct) + '">' +
        '<span class="ajsv__bar-fill" style="width:' + pct.toFixed(1) + '%"></span>' +
      '</div>';
  };

  Survey.prototype.introHTML = function () {
    var c = this.cfg.intro || {};
    var vis = this.visible();
    var answered = vis.filter(this.hasAnswer, this).length;
    var name = this.firstName();
    var eyebrow = name && c.eyebrow_named ? fill(c.eyebrow_named, { name: name }) : c.eyebrow;
    var note = fill(c.note, { count: vis.length, minutes: Math.max(1, Math.round(vis.length * 10 / 60)) });
    var cta;
    if (!this.state.done && answered > 0) {
      cta = '<div class="ajsv__resume">' +
          '<p class="ajsv__resume-note">Welcome back' + (name ? ', ' + esc(name) : '') + '! You already answered ' + answered + ' of ' + vis.length + ' questions.</p>' +
          '<button type="button" class="ajsv__btn" data-ajsv-resume>Pick up where I left off ' + ARROW + '</button>' +
          '<button type="button" class="ajsv__link" data-ajsv-restart>Start over</button>' +
        '</div>';
    } else {
      cta = '<div class="ajsv__intro-cta">' +
          '<button type="button" class="ajsv__btn" data-ajsv-start>' + esc(c.button || 'Start my survey') + ' ' + ARROW + '</button>' +
          (note ? '<p class="ajsv__note">' + esc(note) + '</p>' : '') +
        '</div>';
    }
    return '<div class="ajsv__intro">' +
        '<div class="ajsv__intro-copy">' +
          (eyebrow ? '<span class="ajsv__eyebrow">' + esc(eyebrow) + '</span>' : '') +
          '<h1 class="ajsv__mega" tabindex="-1" data-ajsv-focus>' + esc(c.line1) + '<br><span class="ajsv__redbox">' + esc(c.line2) + '</span></h1>' +
          (c.sub ? '<p class="ajsv__lede">' + esc(c.sub) + '</p>' : '') +
          (c.reward ? '<div class="ajsv__reward"><span class="ajsv__reward-ico">' + GIFT + '</span>' +
            '<p class="ajsv__reward-txt"><strong>Your thank-you</strong>' + esc(c.reward) + '</p></div>' : '') +
          cta +
        '</div>' +
        (c.image ? '<div class="ajsv__intro-media"><img class="ajsv__intro-img" src="' + esc(c.image) + '" alt="" width="440" height="362"></div>' : '') +
      '</div>';
  };

  Survey.prototype.helpFor = function (q) {
    if (q.help) return q.help;
    if (q.kind === 'multi') return q.max ? 'Pick up to ' + q.max + ', then tap Continue.' : 'Tap all that apply, then tap Continue.';
    if (q.kind === 'dropdown') return 'Choose from the list, then tap Continue.';
    if (q.kind === 'scale') return 'Tap a number. 0 means not likely at all. 10 means extremely likely.';
    if (q.kind === 'text') return 'Type your answer, or skip this one.';
    return 'Tap one answer.';
  };

  Survey.prototype.questionHTML = function (q) {
    this.prefill(q);
    var vis = this.visible();
    var pos = vis.indexOf(q) + 1;
    var total = vis.length;
    var groups = this.groups();
    var gi = groups.indexOf(q.group) + 1;
    var firstOfGroup = vis[pos - 2] ? vis[pos - 2].group !== q.group : true;
    var chip = '';
    if (total >= 8 && pos === Math.floor(total / 2) + 1) chip = '&#9733; Halfway there! Your ' + esc(this.cfg.offer || 'discount') + ' is waiting.';
    else if (gi === groups.length && firstOfGroup && groups.length > 1) chip = '&#9733; Last part. You are almost done!';

    var body = '';
    if (q.kind === 'dropdown') body = this.dropdownHTML(q);
    else if (q.kind === 'text') body = this.textHTML(q);
    else if (q.kind === 'scale') body = this.scaleHTML(q);
    else body = this.choicesHTML(q);

    var last = this.isLast(q);
    var finishing = last && !this.missing().length;
    var label = finishing ? ((this.cfg.contact || {}).finish_button || 'Finish') : 'Continue';
    return this.topHTML('Question ' + pos + ' of ' + total, (pos - 1) / total * 100) +
      (chip ? '<p class="ajsv__chip">' + chip + '</p>' : '') +
      (q.group ? '<span class="ajsv__eyebrow">&#9733; Part ' + gi + ' of ' + groups.length + ' &middot; ' + esc(q.group) + '</span>' : '') +
      '<h2 class="ajsv__q" id="ajsv-q-' + esc(q.key) + '" tabindex="-1" data-ajsv-focus>' + esc(q.q) + '</h2>' +
      '<p class="ajsv__help">' + esc(this.helpFor(q)) + '</p>' +
      body +
      '<div class="ajsv__actions">' +
        '<button type="button" class="ajsv__btn" data-ajsv-next' + (this.canContinue(q) ? '' : ' disabled') + '>' + esc(label) + ' ' + ARROW + '</button>' +
        (q.optional ? '<div class="ajsv__actions-row"><button type="button" class="ajsv__link" data-ajsv-skip>Skip this question</button></div>' : '') +
      '</div>';
  };

  Survey.prototype.choicesHTML = function (q) {
    var multi = q.kind === 'multi';
    var cur = this.answer(q);
    var opts = this.options(q);
    var cls = 'ajsv__opts';
    if (q.layout === 'grid') cls += ' ajsv__opts--grid';
    else if (opts.length > 6 && opts.every(function (o) { return o.length <= 34; })) cls += ' ajsv__opts--auto2';
    var html = '<div class="' + cls + '" role="' + (multi ? 'group' : 'radiogroup') + '" aria-labelledby="ajsv-q-' + esc(q.key) + '">';
    for (var i = 0; i < opts.length; i++) {
      var on = multi ? (Array.isArray(cur) && cur.indexOf(opts[i]) > -1) : cur === opts[i];
      html += '<button type="button" class="ajsv__opt" role="' + (multi ? 'checkbox' : 'radio') + '" aria-checked="' + on + '" data-ajsv-opt="' + esc(opts[i]) + '">' +
        '<span class="ajsv__mark' + (multi ? ' ajsv__mark--box' : '') + '">' + CHECK + '</span>' +
        '<span class="ajsv__opt-label">' + esc(opts[i]) + '</span>' +
      '</button>';
    }
    html += '</div>';
    if (multi) html += '<p class="ajsv__picked" data-ajsv-picked aria-live="polite">' + this.pickedText(q) + '</p>';
    return html;
  };

  Survey.prototype.pickedText = function (q) {
    var n = Array.isArray(this.answer(q)) ? this.answer(q).length : 0;
    if (q.max && n >= q.max) return 'You picked ' + n + ' of ' + q.max + '. Tap one you picked to change it.';
    if (q.max) return n ? 'You picked ' + n + ' of ' + q.max + '.' : '&nbsp;';
    return n ? 'You picked ' + n + '.' : '&nbsp;';
  };

  Survey.prototype.scaleHTML = function (q) {
    var cur = this.answer(q);
    var html = '<div class="ajsv__scale" role="radiogroup" aria-labelledby="ajsv-q-' + esc(q.key) + '">';
    for (var i = 0; i <= 10; i++) {
      html += '<button type="button" class="ajsv__opt" role="radio" aria-checked="' + (String(cur) === String(i)) + '" data-ajsv-opt="' + i + '">' + i + '</button>';
    }
    return html + '</div><div class="ajsv__scale-lab" aria-hidden="true"><span>0 = Not likely</span><span>10 = Extremely likely</span></div>';
  };

  Survey.prototype.dropdownHTML = function (q) {
    var cur = this.answer(q) || '';
    var id = 'ajsv-sel-' + esc(q.key);
    var pre = this.state.prefilled[q.key] && this.state.prefilled[q.key] === cur;
    var html = '<div class="ajsv__field"><label class="ajsv__field-label" for="' + id + '">Choose one</label>' +
      '<select class="ajsv__select' + (cur ? '' : ' is-empty') + '" id="' + id + '" data-ajsv-select>' +
      '<option value=""' + (cur ? '' : ' selected') + '>Tap here to choose&hellip;</option>';
    for (var i = 0; i < q.opts.length; i++) {
      html += '<option value="' + esc(q.opts[i]) + '"' + (cur === q.opts[i] ? ' selected' : '') + '>' + esc(q.opts[i]) + '</option>';
    }
    html += '</select>';
    if (pre) html += '<p class="ajsv__prefilled" data-ajsv-prefilled>&#10003; We filled this in from your account. Change it if it&rsquo;s wrong.</p>';
    return html + '</div>';
  };

  Survey.prototype.textHTML = function (q) {
    var id = 'ajsv-txt-' + esc(q.key);
    return '<div class="ajsv__field"><label class="ajsv__field-label" for="' + id + '">Your answer</label>' +
      '<textarea class="ajsv__textarea" id="' + id + '" rows="4" maxlength="500" data-ajsv-text placeholder="Type here&hellip;">' + esc(this.answer(q) || '') + '</textarea></div>';
  };

  Survey.prototype.contactHTML = function () {
    var c = this.cfg.contact || {};
    var need = this.missing();
    var v = this.state.contact;
    var name = this.firstName();
    var title = this.cust.first_name && c.title_named ? fill(c.title_named, { name: name }) : c.title;
    var k = this.cfg.klaviyo || {};
    var optin = this.state.optin == null ? c.optin_default !== false : this.state.optin;
    function field(key, label, type, attrs, full) {
      if (need.indexOf(key) < 0) return '';
      return '<div class="ajsv__field' + (full ? ' ajsv__field--full' : '') + '">' +
        '<label class="ajsv__field-label" for="ajsv-' + key + '">' + label + '</label>' +
        '<input class="ajsv__input" id="ajsv-' + key + '" type="' + type + '" name="' + key + '" data-ajsv-field="' + key + '" value="' + esc(v[key] || '') + '" ' + attrs + '>' +
        '<p class="ajsv__error" data-ajsv-err="' + key + '" hidden></p>' +
      '</div>';
    }
    var nameFields = field('first_name', 'First name', 'text', 'autocomplete="given-name" autocapitalize="words"', false) +
      field('last_name', 'Last name', 'text', 'autocomplete="family-name" autocapitalize="words"', false);
    var bothNames = need.indexOf('first_name') > -1 && need.indexOf('last_name') > -1;
    return this.topHTML('Last step', 100) +
      (c.eyebrow ? '<span class="ajsv__eyebrow">' + esc(c.eyebrow) + '</span>' : '') +
      '<h2 class="ajsv__q" tabindex="-1" data-ajsv-focus>' + esc(title) + '</h2>' +
      (c.sub ? '<p class="ajsv__help">' + esc(c.sub) + '</p>' : '') +
      '<form class="ajsv__form" novalidate data-ajsv-form>' +
        (bothNames ? '<div class="ajsv__fields">' + nameFields + '</div>' : nameFields.replace('ajsv__field"', 'ajsv__field ajsv__field--full"')) +
        field('email', 'Email address', 'email', 'autocomplete="email" inputmode="email" autocapitalize="off" spellcheck="false" placeholder="you@example.com"', true) +
        field('phone', 'Mobile phone', 'tel', 'autocomplete="tel-national" inputmode="tel" placeholder="(555) 555-5555"', true) +
        (k.list_id && c.optin_label ? '<label class="ajsv__check"><input type="checkbox" data-ajsv-optin' + (optin ? ' checked' : '') + '><span>' + esc(c.optin_label) + '</span></label>' : '') +
        '<div class="ajsv__actions">' +
          '<button type="submit" class="ajsv__btn" data-ajsv-submit>' + esc(c.button || 'Unlock my discount') + ' ' + ARROW + '</button>' +
        '</div>' +
      '</form>';
  };

  Survey.prototype.shopHref = function () {
    var t = this.cfg.thanks || {};
    var path = t.shop_path || '/';
    if (t.apply_code && this.cfg.code) return '/discount/' + encodeURIComponent(this.cfg.code) + '?redirect=' + encodeURIComponent(path);
    return path;
  };

  Survey.prototype.doneHTML = function () {
    var t = this.cfg.thanks || {};
    var code = this.cfg.code || '';
    var name = this.firstName();
    var line1 = name && t.line1_named ? fill(t.line1_named, { name: name }) : t.line1;
    var offer = String(this.cfg.offer || '20% OFF');
    var big = offer.replace(/\s*off\s*$/i, '') || offer;
    var debug = '';
    if (this.cfg.dry_run && this.lastPayload) {
      debug = '<details class="ajsv__debug"><summary>Preview only: what gets sent to Klaviyo</summary><pre>' +
        esc(JSON.stringify(this.lastPayload, null, 2)) + '</pre></details>';
    }
    return '<div class="ajsv__done">' +
        '<span class="ajsv__badge">' + BIG_CHECK + '</span>' +
        (t.eyebrow ? '<span class="ajsv__eyebrow">' + esc(t.eyebrow) + '</span>' : '') +
        '<h1 class="ajsv__mega" tabindex="-1" data-ajsv-focus>' + esc(line1) + '<br><span class="ajsv__redbox">' + esc(t.line2) + '</span></h1>' +
        (t.body ? '<p class="ajsv__lede">' + esc(t.body) + '</p>' : '') +
        (code ? '<div class="ajsv__ticket">' +
            '<div class="ajsv__ticket-left">' +
              '<span class="ajsv__ticket-big">' + esc(big) + '</span>' +
              (big !== offer ? '<span class="ajsv__ticket-off">OFF</span>' : '') +
              '<span class="ajsv__ticket-sub">Your next order</span>' +
            '</div>' +
            '<div class="ajsv__ticket-right">' +
              '<span class="ajsv__ticket-lab">Your code</span>' +
              '<div class="ajsv__codebox">' +
                '<span class="ajsv__code-val" data-ajsv-code>' + esc(code) + '</span>' +
                '<button type="button" class="ajsv__copy" data-ajsv-copy aria-live="polite">' + COPY + '<span data-ajsv-copy-label>Copy code</span></button>' +
              '</div>' +
              (t.copy_note ? '<p class="ajsv__ticket-note">' + esc(t.copy_note) + '</p>' : '') +
            '</div>' +
          '</div>' : '') +
        '<div class="ajsv__done-cta"><a class="ajsv__btn" href="' + esc(this.shopHref()) + '" data-ajsv-apply>' + esc(t.apply_button || 'Shop now') + ' ' + ARROW + '</a></div>' +
        (t.apply_note && t.apply_code ? '<p class="ajsv__done-note">' + esc(t.apply_note) + '</p>' : '') +
        (t.disclaimer ? '<p class="ajsv__fine">' + esc(t.disclaimer) + '</p>' : '') +
        debug +
      '</div>';
  };

  /* ---------- interaction ---------- */

  Survey.prototype.canContinue = function (q) {
    if (q.optional || q.kind === 'text') return true;
    return this.hasAnswer(q);
  };

  Survey.prototype.refresh = function (q) {
    var cur = this.answer(q);
    var multi = q.kind === 'multi';
    var full = multi && q.max && Array.isArray(cur) && cur.length >= q.max;
    var btns = this.stage.querySelectorAll('[data-ajsv-opt]');
    for (var i = 0; i < btns.length; i++) {
      var v = btns[i].getAttribute('data-ajsv-opt');
      var on = multi ? (Array.isArray(cur) && cur.indexOf(v) > -1) : String(cur) === v;
      btns[i].setAttribute('aria-checked', on ? 'true' : 'false');
      btns[i].classList.toggle('is-dim', !!(full && !on));
    }
    var picked = this.stage.querySelector('[data-ajsv-picked]');
    if (picked) picked.innerHTML = this.pickedText(q);
    var next = this.stage.querySelector('[data-ajsv-next]');
    if (next) next.disabled = !this.canContinue(q);
  };

  Survey.prototype.pick = function (q, val) {
    var self = this;
    if (q.kind === 'multi') {
      var arr = Array.isArray(this.answer(q)) ? this.answer(q).slice() : [];
      var at = arr.indexOf(val);
      if (at > -1) arr.splice(at, 1);
      else if (EXCLUSIVE.test(val)) arr = [val];
      else {
        arr = arr.filter(function (v) { return !EXCLUSIVE.test(v); });
        if (q.max && arr.length >= q.max) { this.refresh(q); return; }
        arr.push(val);
      }
      if (arr.length) this.state.answers[q.key] = arr; else delete this.state.answers[q.key];
      this.save();
      this.refresh(q);
      return;
    }
    this.state.answers[q.key] = q.kind === 'scale' ? parseInt(val, 10) : val;
    this.save();
    this.refresh(q);
    clearTimeout(this.timer);
    // The last question never auto-submits: they press the Finish button themselves
    if (!this.isLast(q)) this.timer = setTimeout(function () { self.next(); }, AUTO_ADVANCE_MS);
  };

  Survey.prototype.bind = function () {
    var self = this;
    var st = this.stage;
    var q = this.view === 'q' ? this.qs[this.state.idx] : null;

    function on(sel, fn) {
      var el = st.querySelector(sel);
      if (el) el.addEventListener('click', fn);
    }
    on('[data-ajsv-start]', function () { self.start(false); });
    on('[data-ajsv-resume]', function () { self.start(true); });
    on('[data-ajsv-restart]', function () {
      self.state = self.fresh();
      self.save();
      self.render(false);
    });
    on('[data-ajsv-back]', function () { self.back(); });
    on('[data-ajsv-next]', function () { if (q && self.canContinue(q)) self.next(); });
    on('[data-ajsv-skip]', function () { if (q) { delete self.state.answers[q.key]; self.save(); } self.next(); });

    if (q) {
      var opts = st.querySelectorAll('[data-ajsv-opt]');
      for (var i = 0; i < opts.length; i++) {
        opts[i].addEventListener('click', function () { self.pick(q, this.getAttribute('data-ajsv-opt')); });
      }
      var sel = st.querySelector('[data-ajsv-select]');
      if (sel) sel.addEventListener('change', function () {
        if (this.value) self.state.answers[q.key] = this.value; else delete self.state.answers[q.key];
        this.classList.toggle('is-empty', !this.value);
        var note = st.querySelector('[data-ajsv-prefilled]');
        if (note && this.value !== self.state.prefilled[q.key]) note.parentNode.removeChild(note);
        self.save();
        self.refresh(q);
      });
      var txt = st.querySelector('[data-ajsv-text]');
      if (txt) txt.addEventListener('input', function () {
        if (this.value.trim()) self.state.answers[q.key] = this.value; else delete self.state.answers[q.key];
        self.save();
      });
    }

    var form = st.querySelector('[data-ajsv-form]');
    if (form) {
      var inputs = form.querySelectorAll('[data-ajsv-field]');
      for (var j = 0; j < inputs.length; j++) {
        inputs[j].addEventListener('input', function () {
          var key = this.getAttribute('data-ajsv-field');
          // Format the phone as they type, but only while the cursor is at the end
          if (key === 'phone' && this.selectionStart === this.value.length) {
            var pretty = prettyPhone(this.value);
            if (pretty !== this.value) this.value = pretty;
          }
          self.state.contact[key] = this.value;
          self.save();
        });
      }
      form.addEventListener('submit', function (e) { e.preventDefault(); self.submitContact(form); });
    }

    on('[data-ajsv-copy]', function () { self.copyCode(this); });
  };

  Survey.prototype.bindKeys = function () {
    var self = this;
    document.addEventListener('keydown', function (e) {
      if (self.view !== 'q' || e.altKey || e.ctrlKey || e.metaKey) return;
      if (!self.root.contains(document.activeElement) && document.activeElement !== document.body) return;
      // Buttons, links and fields handle Enter natively; only catch it when focus is on the heading/page
      if (e.target.closest && e.target.closest('button, a, input, textarea, select, label')) return;
      var q = self.qs[self.state.idx];
      if (e.key === 'Enter' && !isTyping(e.target) && self.canContinue(q)) { e.preventDefault(); self.next(); }
    });
  };

  Survey.prototype.copyCode = function (btn) {
    var code = this.cfg.code || '';
    var label = btn.querySelector('[data-ajsv-copy-label]') || btn;
    function done() {
      btn.classList.add('is-copied');
      label.textContent = 'Copied!';
      setTimeout(function () { btn.classList.remove('is-copied'); label.textContent = 'Copy code'; }, 2600);
    }
    function fallback() {
      var ta = document.createElement('textarea');
      ta.value = code; ta.setAttribute('readonly', ''); ta.style.position = 'fixed'; ta.style.top = '0'; ta.style.opacity = '0';
      document.body.appendChild(ta); ta.focus(); ta.select();
      try { document.execCommand('copy'); done(); } catch (e) { /* the code is on screen either way */ }
      document.body.removeChild(ta);
    }
    if (navigator.clipboard && window.isSecureContext) navigator.clipboard.writeText(code).then(done, fallback);
    else fallback();
  };

  /* ---------- submit ---------- */

  Survey.prototype.submitContact = function (form) {
    var need = this.missing();
    var v = this.state.contact;
    var errors = {};
    if (need.indexOf('first_name') > -1 && !String(v.first_name || '').trim()) errors.first_name = 'Please type your first name.';
    if (need.indexOf('last_name') > -1 && !String(v.last_name || '').trim()) errors.last_name = 'Please type your last name.';
    if (need.indexOf('email') > -1 && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(v.email || '').trim())) errors.email = 'Please check your email. It should look like name@example.com.';
    if (need.indexOf('phone') > -1 && digits(v.phone).replace(/^1(?=\d{10}$)/, '').length !== 10) errors.phone = 'Please type your 10-digit phone number, like (555) 555-5555.';
    var first = null;
    need.forEach(function (key) {
      var input = form.querySelector('[data-ajsv-field="' + key + '"]');
      var err = form.querySelector('[data-ajsv-err="' + key + '"]');
      if (!input || !err) return;
      if (errors[key]) {
        err.textContent = errors[key]; err.hidden = false; input.classList.add('is-error');
        if (!first) first = input;
      } else { err.hidden = true; input.classList.remove('is-error'); }
    });
    if (first) { first.focus(); return; }
    var optinEl = form.querySelector('[data-ajsv-optin]');
    this.state.optin = optinEl ? optinEl.checked : false;
    this.save();
    this.finish(form.querySelector('[data-ajsv-submit]'));
  };

  // Merge what Shopify knows with what they typed. Shopify wins when it has a value.
  Survey.prototype.person = function () {
    var c = this.cust, v = this.state.contact;
    function pickv(a, b) { a = String(a || '').trim(); return a || String(b || '').trim(); }
    return {
      first_name: titleCase(pickv(c.first_name, v.first_name)),
      last_name: titleCase(pickv(c.last_name, v.last_name)),
      email: pickv(c.email, v.email).toLowerCase(),
      phone: digits(c.phone).length >= 10 ? String(c.phone).trim() : String(v.phone || '').trim(),
      customer_id: String(c.id || '')
    };
  };

  Survey.prototype.answersOnly = function () {
    var props = {};
    var vis = this.visible();
    for (var i = 0; i < vis.length; i++) {
      var q = vis[i];
      if (!this.hasAnswer(q)) continue;
      var a = this.answer(q);
      props[q.key] = Array.isArray(a) ? a.slice() : (typeof a === 'string' ? a.trim() : a);
    }
    return props;
  };

  Survey.prototype.finish = function (btn) {
    var self = this;
    var question = this.view === 'q' ? this.stage.querySelector('[data-ajsv-next]') : null;
    var b = btn || question;
    if (b) { b.disabled = true; b.textContent = 'One moment…'; }
    var finished = false;
    function complete() {
      if (finished) return;
      finished = true;
      self.state.done = true;
      self.save();
      self.go('done');
      if (self.cfg.confetti) confetti();
    }
    // Never hold the code hostage to a slow network
    setTimeout(complete, SEND_WAIT_MS);
    this.send().then(complete, complete);
  };

  Survey.prototype.send = function () {
    var k = this.cfg.klaviyo || {};
    var p = this.person();
    var answers = this.answersOnly();
    var meta = {
      svy_response_id: this.state.rid,
      svy_completed_at: new Date().toISOString(),
      svy_version: this.cfg.storage_key || 'v2',
      svy_logged_in: !!p.customer_id,
      svy_shopify_customer_id: p.customer_id,
      svy_first_name: p.first_name,
      svy_last_name: p.last_name,
      svy_email: p.email,
      svy_phone: p.phone
    };
    var eventProps = {};
    var key;
    for (key in answers) eventProps[key] = answers[key];
    for (key in meta) eventProps[key] = meta[key];
    var profileProps = {};
    for (key in answers) profileProps[key] = answers[key];
    profileProps.svy_completed_at = meta.svy_completed_at;

    var profileAttrs = { email: p.email, properties: profileProps };
    if (p.first_name) profileAttrs.first_name = p.first_name;
    if (p.last_name) profileAttrs.last_name = p.last_name;
    var st = answers.svy_state;
    if (st && !/outside/i.test(st)) profileAttrs.location = { region: st, country: 'United States' };

    var eventBody = {
      data: {
        type: 'event',
        attributes: {
          properties: eventProps,
          unique_id: this.state.rid,
          time: meta.svy_completed_at,
          metric: { data: { type: 'metric', attributes: { name: k.event || 'Completed Customer Survey' } } },
          profile: { data: { type: 'profile', attributes: profileAttrs } }
        }
      }
    };
    var phone = e164(p.phone);
    var phoneBody = phone ? { data: { type: 'profile', attributes: { email: p.email, phone_number: phone } } } : null;
    var subBody = null;
    if (this.state.optin && k.list_id) {
      subBody = {
        data: {
          type: 'subscription',
          attributes: {
            custom_source: 'Customer Survey',
            profile: { data: { type: 'profile', attributes: { email: p.email, subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } } } } }
          },
          relationships: { list: { data: { type: 'list', id: k.list_id } } }
        }
      };
    }

    try {
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish('customer_survey_completed', answers);
      }
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: 'customer_survey_completed' });
    } catch (e) { /* analytics must never block the code */ }

    if (this.cfg.dry_run || !k.company_id || !p.email) {
      this.lastPayload = { event: eventBody, phone: phoneBody || '(no valid US phone, not sent)', subscription: subBody || '(not sent: box unticked or no list ID set)' };
      if (window.console) console.info('[AJ survey] dry run, nothing sent', this.lastPayload);
      return Promise.resolve();
    }

    try { if (window.klaviyo && typeof window.klaviyo.identify === 'function') window.klaviyo.identify({ email: p.email }); } catch (e) { /* noop */ }

    function post(path, body) {
      return fetch('https://a.klaviyo.com/client/' + path + '/?company_id=' + encodeURIComponent(k.company_id), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', revision: KLAVIYO_REVISION },
        body: JSON.stringify(body),
        keepalive: true
      }).then(function (r) {
        if (!r.ok && window.console) console.warn('[AJ survey] Klaviyo ' + path + ' returned ' + r.status);
        return r.ok;
      }, function () { return false; });
    }
    // The event is the record. Phone and subscription ride separately so they can't sink it.
    return post('events', eventBody).then(function (ok) {
      if (!ok) return post('events', eventBody);
    }).then(function () {
      var extra = [];
      if (phoneBody) extra.push(post('profiles', phoneBody));
      if (subBody) extra.push(post('subscriptions', subBody));
      return Promise.all(extra);
    });
  };

  /* ---------- boot ---------- */

  function init(scope) {
    var roots = (scope || document).querySelectorAll('[data-ajsv]');
    for (var i = 0; i < roots.length; i++) {
      if (roots[i].__ajsv) continue;
      try { roots[i].__ajsv = new Survey(roots[i]); } catch (e) { if (window.console) console.error('[AJ survey]', e); }
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { init(); });
  else init();
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
